import { Box, Typography } from "@mui/material"
import { useQueryAdminDashboardStoreItems } from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardPeriodParams,
  IDashboardStoreItemRow,
} from "ts/interfaces/AdminDashboard"

import DashboardSection from "./DashboardSection"
import DashboardSectionBody, {
  RowsSkeleton,
  useSectionState,
} from "./DashboardSectionBody"
import RankedBarRow from "./RankedBarRow"

const MAX_ROWS = 10

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`

const StoreItems = ({ params }: { params: IDashboardPeriodParams }) => {
  const { t } = useTranslation()
  const query = useQueryAdminDashboardStoreItems(params)

  // BED sorts by purchase_count desc and has no limit param, so the cap is FE-side.
  const rows = query.data?.data.rows.slice(0, MAX_ROWS) ?? []
  const top = rows[0]?.purchase_count ?? 0
  const { state } = useSectionState([query], !rows.length)
  const formatSummary = (row: IDashboardStoreItemRow) =>
    `${row.purchase_count.toLocaleString("en-US")} · ${formatAmount(row.purchase_amount)}`
  // One width for every row's right slot, so all bars end in the same place.
  const summaryWidth = `${Math.max(0, ...rows.map((row) => formatSummary(row).length))}ch`

  return (
    <DashboardSection
      title={t("adminDashboard.storeItems")}
      question={t("adminDashboard.storeItemsQuestion")}
      exportImage={{
        section: "store-items",
        dateFrom: query.data?.data.date_from,
        dateTo: query.data?.data.date_to,
        disabled: state !== "ready",
      }}
    >
      <DashboardSectionBody
        queries={[query]}
        isEmpty={!rows.length}
        emptyText={t("adminDashboard.storeItemsEmpty")}
        skeleton={<RowsSkeleton rows={MAX_ROWS} />}
      >
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, pt: "10px" }}>
          {rows.map((row, index) => {
            const count = row.purchase_count.toLocaleString("en-US")
            const amount = formatAmount(row.purchase_amount)
            return (
              <RankedBarRow
                key={row.fk_store_item_id ?? `${row.article_name}-${index}`}
                rank={index + 1}
                label={row.article_name}
                labelColor="mockup.neutral10"
                labelWeight={500}
                ratio={top > 0 ? row.purchase_count / top : 0}
                barColor="warning.main"
                right={
                  <Typography
                    variant="body3"
                    color="mockup.secondary30"
                    sx={{
                      width: summaryWidth,
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                      fontSize: "14px",
                      fontWeight: 700,
                    }}
                  >
                    <span aria-hidden="true">{formatSummary(row)}</span>
                    <Box component="span" sx={visuallyHiddenSx}>
                      {t("adminDashboard.storeItemRowSummary", {
                        purchases: count,
                        amount,
                      })}
                    </Box>
                  </Typography>
                }
              />
            )
          })}
        </Box>
      </DashboardSectionBody>
    </DashboardSection>
  )
}

export default StoreItems
